import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'dark' | 'light' | 'system'

const STORAGE_KEY = 'tracker-theme'

function readPreference(): ThemePreference {
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'dark'
}

function resolve(preference: ThemePreference): 'dark' | 'light' {
  if (preference !== 'system') return preference
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/**
 * Theme preference (ui-spec §5.5). Dark is the default and the one the design was drawn in; light
 * is the same layout on the other half of tokens.css.
 *
 * The attribute goes on `<html>`, not on a wrapper, so it also covers what renders outside the
 * React tree — the page background before hydration, and the browser's own form controls.
 */
export function useTheme(): {
  preference: ThemePreference
  setPreference: (next: ThemePreference) => void
} {
  const [preference, setState] = useState<ThemePreference>(readPreference)

  useEffect(() => {
    applyTheme(preference)
    if (preference !== 'system') return

    // Following the system means following it while the app is open, not only at load.
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (): void => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    window.localStorage.setItem(STORAGE_KEY, next)
    setState(next)
  }, [])

  return { preference, setPreference }
}

function applyTheme(preference: ThemePreference): void {
  document.documentElement.setAttribute('data-theme', resolve(preference))
}

/**
 * Called from main.tsx before React renders. Without it the app paints dark for one frame and then
 * flips, which reads as a bug rather than as a preference being honoured.
 */
export function applyStoredTheme(): void {
  applyTheme(readPreference())
}
